/**
 * CalmModal — the full-screen Quick Calm modal: breath circle, sound toggle,
 * start/stop button, and rotating supportive guidance text.
 *
 * Owns no breath state itself — composes `useBreathCycle` + `useCalmAudio`.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pause, Volume2, VolumeX } from 'lucide-react';
import {
  FADE_ANIMATE, FADE_DOWN_20, FADE_IN, FADE_OUT, FADE_UP_10,
  FADE_Y_ANIMATE, SCALE_ANIMATE_ALT, SCALE_IN_90,
} from '../../animations';
import BreathCircle from './BreathCircle';
import { useBreathCycle } from './useBreathCycle';
import { useCalmAudio } from './useCalmAudio';

const PHASE_GUIDANCE = {
  inhale: 'Draw breath deep into your belly. Feel expansion.',
  hold: 'Rest in stillness. You are safe in this moment.',
  exhale: "Release tension. Let go of what doesn't serve you.",
};

const CalmModal = ({ onClose }) => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audio = useCalmAudio();
  const cycle = useBreathCycle(() => audio.stop());

  const startSession = () => {
    cycle.start();
    if (soundEnabled) audio.start();
  };
  const stopSession = () => {
    cycle.stop();
    audio.stop();
  };
  const handleClose = () => {
    stopSession();
    onClose();
  };

  return (
    <motion.div
      initial={FADE_IN}
      animate={FADE_ANIMATE}
      exit={FADE_OUT}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
      data-testid="quick-calm-modal"
    >
      <motion.div
        initial={SCALE_IN_90}
        animate={SCALE_ANIMATE_ALT}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-white/60 hover:text-white transition-colors"
          data-testid="close-quick-calm"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center">
          <motion.h2
            initial={FADE_DOWN_20}
            animate={FADE_Y_ANIMATE}
            className="text-2xl font-medium text-white mb-2"
          >
            {cycle.isActive ? 'Find Your Center' : 'Quick Calm'}
          </motion.h2>
          <p className="text-white/60 text-sm mb-8">
            {cycle.isActive ? `${cycle.totalTime}s remaining` : '60-second reset with breath & sound'}
          </p>

          <BreathCircle phase={cycle.phase} countdown={cycle.countdown} isActive={cycle.isActive} />

          {/* Sound Toggle */}
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setSoundEnabled((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                soundEnabled
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : 'bg-white/5 text-white/50 border border-white/10'
              }`}
              data-testid="toggle-calm-sound"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              528Hz {soundEnabled ? 'On' : 'Off'}
            </button>
          </div>

          {/* Action Button */}
          {!cycle.isActive ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={startSession}
              className="w-full max-w-xs mx-auto bg-white text-black font-medium py-4 rounded-full transition-all shadow-lg"
              data-testid="start-calm-session"
            >
              Begin 60-Second Reset
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={stopSession}
              className="w-full max-w-xs mx-auto bg-white/10 text-white font-medium py-4 rounded-full transition-all border border-white/20 flex items-center justify-center gap-2"
              data-testid="stop-calm-session"
            >
              <Pause className="w-5 h-5" />
              End Session
            </motion.button>
          )}

          {/* Rotating Guidance */}
          <AnimatePresence mode="wait">
            {cycle.isActive && (
              <motion.p
                key={cycle.phase}
                initial={FADE_UP_10}
                animate={FADE_Y_ANIMATE}
                exit={{ opacity: 0, y: -10 }}
                className="mt-8 text-white/70 text-sm max-w-xs mx-auto"
              >
                {PHASE_GUIDANCE[cycle.phase]}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CalmModal;
